<template>
  <section
    v-observe-visibility="{
      callback: () => visibilityChanged(true),
      once: true,
    }"
    class="inline-gallery cs-gallery"
    :class="{
      'gallery-in-use': galleryInUse,
      'has-fullscreen': fullscreen,
      desktop: $isDesktop,
    }"
    @click="galleryClick"
  >
    <pu-flickity
      v-if="!isSearchUrl || searchStore.states.includes('results')"
      ref="flickity"
      :key="'inline-gallery-' + galleryPhotos.length"
      :options="flickityOptions"
      :start="isVisible"
      :style="{ opacity: showFlickity ? 1 : 0 }"
      @init="initGallery"
    >
      <div
        v-for="(photo, index) in galleryPhotos"
        :key="`photo-${index}`"
        ref="flickity-photos"
        class="carousel-cell"
        v-on="fullscreen && !fullscreenOpen ? { click: openFullscreen } : {}"
      >
        <img
          loading="lazy"
          :data-photo-index="index"
          v-bind="getImgAttrs(photo, index)"
          :data-caption="
            $i18n.locale.split('-')[0] === 'en'
              ? capitalize(photo.caption)
              : capitalize(photo.titleAttribution)
          "
          :alt="capitalize(photoCaptionAltFormatter(photo.caption))"
          :data-sctrack="`${String($route.name)}--InlineGallery-img-desktop`"
        />
      </div>
      <div v-show="isFullScreen" class="fullscreen-header">
        <i class="photo-count photo-count-fs"
          >{{ currentImage }} / {{ photoCount ? photoCount : photos.length }}</i
        >
      </div>
      <div class="caption" />
    </pu-flickity>
    <div v-show="photos.length > 1 && photoCount !== 0" class="gallery-footer">
      <div class="photo-count-wrapper">
        <i class="photo-count"
          >{{ currentImage }}/{{ photoCount ? photoCount : photos.length }}</i
        >
      </div>
      <div class="gallery-scrollbar">
        <span
          :style="{
            width:
              (100 / (photoCount ? photoCount : photos.length)) * currentImage +
              '%',
          }"
          class="scrollbar-pip"
        />
      </div>
      <div class="album-indication" />
    </div>
  </section>
</template>

<script lang="ts">
import type { RouteLocationNormalized } from 'vue-router'
import { PitchupPhoto } from '~/common/photos/types'
import { ignoreTouchesWhenSwiping } from '~/utility/flickity'
import { photoCaptionAltFormatter } from '~/utility/photoCaptionAltFormatter'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { capitalize } from '~/utility/text'
import type PuFlickity from '../../../components/PuFlickity.vue'
import { transformImageSrcSet } from '../../../utility/image-url'
import { logger } from '../../../utility/logger'
import { isSearchUrlRoute } from '../../search/isSearchUrlComponent'
import { Photo } from '../../search/schemas'

@Component({
  emits: ['gallery-loaded'],
  setup() {
    // since we are doing a search anyway, let's use the search results to initilise the first set of markers
    const searchStore = useSearchStoreSetup(usePinia())
    return { searchStore }
  },
})
class InlineGallery extends Vue {
  declare $refs: { flickity: InstanceType<typeof PuFlickity> }
  searchStore: ReturnType<typeof useSearchStoreSetup>

  @Prop({ required: false })
  primaryPhoto: Photo

  @Prop({ required: true })
  photos: PitchupPhoto[]

  @Prop({ required: false })
  photoCount: number

  @Prop()
  searchResultIndex: number

  @Prop({ default: false })
  isSearchResults: boolean

  @Prop({ required: false })
  galleryInitialIndex: number

  @Prop({ default: true })
  limitInitialGalleryPhotos: boolean

  @Prop({ required: false })
  useHash: boolean

  get isSearchUrl() {
    return isSearchUrlRoute(this.$route, [])
  }

  get galleryPhotos() {
    // ensure primary is first
    const photos: PitchupPhoto[] = []
    if (this.primaryPhoto) {
      photos.push(this.primaryPhoto)
    }
    ;(this.photos || []).forEach((photo) => {
      if (
        !photos[0] ||
        this.isDifferentPhoto(photo.url.masterImage, photos[0].url.masterImage)
      ) {
        photos.push(photo)
      }
    })
    // Filter photos to only include those with a url.masterImage
    return photos
      .filter((photo) => photo.url && photo.url.masterImage)
      .slice(
        0,
        this.showFlickity || !this.limitInitialGalleryPhotos ? undefined : 8,
      )
  }

  isDifferentPhoto(firstUrl: string, secondUrl: string) {
    const firstPhoto = firstUrl.split('/')
    const secondPhoto = secondUrl.split('/')
    return (
      firstPhoto[firstPhoto.length - 1] !== secondPhoto[secondPhoto.length - 1]
    )
  }

  @Prop({ default: false })
  fullscreen!: boolean

  @Prop({ default: false })
  fullscreenOpen: boolean

  currentImage: number = 1

  get flickityOptions() {
    return {
      initialIndex: this.currentImage - 1,
      dragThreshold: 20,
      pageDots: false,
      wrapAround: true,
      cellSelector: '.carousel-cell',
      selectedAttraction: 0.2,
      friction: 0.8,
      fullscreen: this.fullscreen,
      lazyLoad: 1,
      prevNextButtons: this.$isDesktop,
      draggable: !this.$isDesktop,
    }
  }

  gallery: any = null
  caption: any = null
  isFullScreen = false
  selectedImage: any = null
  galleryInUse = false
  dragging = false
  animationTimer: any = null
  viewFullscreenButton: HTMLElement | null = null
  exitFullscreenButton: HTMLElement | null = null

  mounted() {
    if (this.galleryInitialIndex) {
      this.currentImage = this.galleryInitialIndex + 1
    }
  }

  /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- DOM event with srcElement is untyped */
  galleryClick(event: any) {
    if (this.$isDesktop && !this.fullscreen) {
      if (
        event.srcElement.tagName === 'svg' ||
        event.srcElement.tagName === 'path' ||
        event.srcElement.tagName === 'BUTTON'
      ) {
        event.stopPropagation()
        event.preventDefault()
      }
    }
  }
  /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

  getSizesAttribute() {
    if (this.$isDesktop && !this.isFullScreen) {
      return this.isSearchResults ? '25vw' : '45vw'
    } else {
      return '100vw'
    }
  }

  getSrcsetAttribute(photo: PitchupPhoto) {
    if (this.$isDesktop && this.isFullScreen) {
      return `/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=720,h=540${photo.url.masterImage} 840w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1080,h=810${photo.url.masterImage} 1080w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1800,h=1350${photo.url.masterImage} 1800w`
    } else if (!this.$isDesktop && this.isFullScreen) {
      return `/_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=720,h=540${photo.url.masterImage} 840w, /_cfi/cdn-cgi/image/format=auto,fit=scale-down,quality=60,w=1080,h=810${photo.url.masterImage} 1080w`
    } else {
      return `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${photo.url.masterImage} 360w, /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=720,h=540${photo.url.masterImage} 840w, /_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=1080,h=810${photo.url.masterImage} 1080w`
    }
  }

  getImgAttrs(photo: PitchupPhoto, index: number) {
    const common = {
      sizes: this.getSizesAttribute(),
      width: '428',
      height: '321',
    }
    const srcset = this.getSrcsetAttribute(photo)

    if (this.searchResultIndex !== 0 || index !== 0) {
      return {
        src: `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${photo.url.masterImage}`,
        srcset: transformImageSrcSet(srcset),
        ...common,
      }
    }
    return {
      src: `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=80,w=360,h=270${photo.url.masterImage}`,
      srcset: transformImageSrcSet(srcset),
      fetchpriority: 'high',
      decoding: 'sync',
      ...common,
    }
  }

  showFlickity = false

  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- Flickity instance is untyped */
  async initGallery(flickityInstance: any) {
    this.gallery = this.$el
    this.caption = this.gallery.querySelector('.caption')
    this.initHandlers(flickityInstance)
    this.viewFullscreenButton = flickityInstance.viewFullscreenButton?.element
    this.exitFullscreenButton = flickityInstance.exitFullscreenButton?.element
    if (this.viewFullscreenButton) {
      this.viewFullscreenButton.addEventListener('click', () => {
        void this.openFullscreen()
      })
    }
    if (this.$route.hash === `#${this.fullscreenHash}` || this.fullscreenOpen) {
      this.visibilityChanged(true)
      this.launchFullscreenGallery()
    }
    flickityInstance.resize()
    await this.$nextTick(() => {
      this.showFlickity = true
      this.$emit('gallery-loaded')
    })
  }
  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

  /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Flickity event handlers are untyped */
  initHandlers(f: any) {
    f.on('change', () => this.updateSlideStatus())
    f.on('select', () => this.updateCaption())
    f.on(
      'fullscreenChange',
      async (fullscreenStatus: boolean) =>
        await this.fullscreenChange(fullscreenStatus),
    )
    f.on('dragStart', () => this.dragStart())
    f.on('dragEnd', () => this.dragEnd())
    f.once('dragStart', () => {
      this.$emit('user-has-interacted')
    })
    ignoreTouchesWhenSwiping(this.gallery)
    f.on('staticClick', (event: any) => {
      if (event.target.tagName !== 'IMG') {
        this.closeFullscreen()
      }
    })
  }
  /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */

  /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Gallery DOM element is untyped */
  async fullscreenChange(fullscreenStatus: boolean) {
    logger('fullscreenChange', fullscreenStatus)
    this.isFullScreen = fullscreenStatus
    await this.$nextTick()
    this.reactivateFlickity()
    this.updateCaption()
    this.updateSlideStatus()
    this.isFullScreen
      ? this.gallery.setAttribute('gallery-open', 'true')
      : this.gallery.removeAttribute('gallery-open')

    if (this.isFullScreen) {
      this.exitFullscreenButton?.addEventListener('click', this.closeFullscreen)
    }
    if (!fullscreenStatus) {
      this.$emit('fullscreen-closed')
    }
  }
  /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */

  reactivateFlickity() {
    if (this.isFullScreen) {
      document.documentElement.classList.add('is-flickity-fullscreen')
    }
  }

  unmounted() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer)
      this.animationTimer = null
    }
    if (this && document && document.documentElement) {
      document.documentElement.classList.remove('is-flickity-fullscreen')
    }
  }

  fullscreenHash = '#photo-gallery'

  @Watch('$route')
  routeChange(to: RouteLocationNormalized, from?: RouteLocationNormalized) {
    if (to.path === from?.path) {
      to.meta.scroll = false
    }
    const galleryHash = this.fullscreenHash
    if (to.hash === galleryHash) {
      this.launchFullscreenGallery()
    } else if (from?.hash === galleryHash && !to.hash.includes(galleryHash)) {
      this.closeFullscreenGallery()
    }
  }

  async openFullscreen() {
    logger('openFullscreen', this.useHash)
    await this.$router.push({
      hash: this.fullscreenHash,
      query: this.$router.currentRoute.value.query,
    })
  }

  closeFullscreen() {
    logger('closeFullscreen', this.useHash)
    if (this.useHash) {
      void (this.$routerClient.getReferrer()
        ? this.$router.back()
        : this.$router.push({
            query: this.$router.currentRoute.value.query,
          }))
    } else {
      this.closeFullscreenGallery()
    }
  }

  /* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Flickity ref and gallery DOM are untyped */
  get flickityInstance() {
    if (this.$refs && this.$refs.flickity && this.$refs.flickity.flickity) {
      return this.$refs.flickity.flickity()
    }
    return undefined
  }

  launchFullscreenGallery() {
    if (this.fullscreen && this.gallery) {
      this.flickityInstance?.viewFullscreen?.()
    }
  }

  closeFullscreenGallery() {
    logger('closeFullscreenGallery')
    this.flickityInstance?.exitFullscreen?.()
  }

  updateSlideStatus() {
    const slideIndex = this.$refs.flickity?.selectedIndex() + 1
    this.currentImage = slideIndex
    this.$emit('slide-changed', this.currentImage)
  }

  updateCaption() {
    this.selectedImage = this.gallery.querySelector(
      '.carousel-cell.is-selected img',
    )
    if (this.isFullScreen && this.caption && this.selectedImage) {
      this.caption.textContent = this.selectedImage.getAttribute('data-caption')
    }
  }
  /* eslint-enable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

  dragStart() {
    clearTimeout(this.animationTimer)
    this.galleryInUse = true
    this.dragging = true
    document.body.classList.add('user-dragging-gallery')
  }

  dragEnd() {
    this.dragging = false
    document.body.classList.remove('user-dragging-gallery')
    this.animationTimer = setTimeout(() => {
      this.galleryInUse = false
    }, 3000)
  }

  capitalize(value: string) {
    return capitalize(value)
  }

  photoCaptionAltFormatter(caption: any) {
    return photoCaptionAltFormatter(caption)
  }

  isVisible = false
  visibilityChanged(isVisible: boolean) {
    this.isVisible = isVisible
  }
}

export default toNative(InlineGallery)
</script>

<style>
@import url('~/assets/css/flickity-custom.css');
@import url('flickity/css/flickity.css');
</style>

<style scoped>
.cs-gallery {
  background-color: transparent;
  background-image: none;
  display: block;
  height: 100%;
  position: relative;
  width: 100%;
}

.carousel-cell {
  width: 100%;
  min-width: 100%;
  height: 69vw;
  display: block;
}

.campsite .carousel-cell {
  height: 75vw;
}

/* .carousel-cell img {
  width: 100%;
  height: auto;
} */

.photo-count-wrapper {
  align-items: center;
  display: flex;
  filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.4));
  flex-direction: row;
  flex-shrink: 0;
}

.photo-count {
  font-size: var(--body);
  color: white;
}

.gallery-footer {
  align-items: center;
  background: url('~/assets/images/background-fade.png') repeat-x bottom left;
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  left: 0;
  padding: 0.3em 1.4em 0.3em 1.2em;
  pointer-events: none;
  position: absolute;
  right: 0;
  transition: background 0.5s ease-in;
}

.gallery-in-use .gallery-footer {
  background-position-y: 3em;
}

.gallery-scrollbar {
  background: rgba(255, 255, 255, 0.35);
  border-radius: 0.3em;
  height: 0.4em;
  margin: 0 0 0 1em;
  pointer-events: none;
  position: relative;
  width: 100%;
}

.has-fullscreen .gallery-scrollbar {
  margin: 0 2em 0 1em;
}

.gallery-scrollbar {
  transition: opacity 0.4s ease-in 0.3s;
  opacity: 1;
}

.gallery-in-use .gallery-scrollbar {
  opacity: 0;
}

.scrollbar-pip {
  background: white;
  border-radius: 0.3em;
  height: 0.4em;
  left: 0;
  min-width: 1em;
  position: absolute;
  top: 0;
  z-index: 1;
}

.gallery-scrollbar {
  margin: 0 2.5em 0 1em;
}

.album-indication {
  background: url('~/assets/images/albums.svg') no-repeat bottom left;
  bottom: 0.65em;
  display: block;
  filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.4));
  padding: 0;
  right: 0.9em;
  top: auto;
  height: 1.8em;
  width: 1.8em;
  position: absolute;
}

.desktop .album-indication {
  height: 2em;
  width: 2em;
  right: 1em;
}

/* Hides DOM element that is not modified by flickity
https://gitlab.pitchup.com/pitchup/alfred/-/issues/3139
*/
.flickity-enabled > .carousel-cell {
  display: none;
}

/* desktop & tablet */
.desktop .flickity-enabled {
  aspect-ratio: 4 / 3;
  overflow: hidden;
}
</style>
